/**
 * Iter C.1.2a — Proposal line-item display expansion.
 *
 * Atomic line items render 1:1 as before. Package line items expand into
 * N "display rows" based on the per-line `package_presentation_mode`:
 *
 *   • itemized      → one row per component
 *   • consolidated  → one row labelled `package_consolidated_label`
 *   • hybrid        → visible component rows + 1 "Bundled services" rollup
 *
 * The expansion is the single source of truth: the in-app line table,
 * the Subtotal/GST/Total footer AND the Year-1 Outflow KPI strip all
 * consume the SAME flattened list, so what the operator sees on screen
 * matches what gets rolled up. Switching presentation mode does NOT
 * change the totals — same commercial value, different presentation.
 *
 * Phase C.1.2b will extend this for the PDF renderer + GST/HSN weighted
 * blend on consolidated rows. C.1.3 will add automatic head `amount`
 * derivation. C.1.2a stops short of both.
 *
 * Software vs AMC split for package components is driven by
 * `billing_frequency`:
 *   • "One-time" → Software & Set-up bucket
 *   • Annual / Monthly / Quarterly → AMC (recurring) bucket
 * This matches the existing atomic-line convention used by the Year-1
 * Outflow KPI strip and keeps the bucket semantics stable.
 */
import { resolveSoftwareFee, resolveAmcFee, resolveAnnualCostFee, isAnnualCostComplimentary } from "./lineItemFees";

/**
 * Bucket a component into Software vs AMC by its billing frequency.
 * Recurring components (Annual/Quarterly/Monthly) feed the AMC bucket
 * for Year-1 outflow purposes; one-time components feed Software.
 */
const _componentSoftwareFee = (c) =>
    (c?.billing_frequency === "One-time" ? Number(c?.amount) || 0 : 0);
const _componentAmcFee = (c) =>
    (c?.billing_frequency !== "One-time" ? Number(c?.amount) || 0 : 0);

/**
 * Returns `true` when this line item carries a package snapshot that
 * the display layer should expand. Defensive — an empty
 * `package_components` array is treated as atomic so a misconfigured
 * package can't blank the table.
 */
export const isPackageLine = (li) =>
    !!(li && li.package_source_id && Array.isArray(li.package_components) && li.package_components.length > 0);

/**
 * Produce display rows for a single line item.
 *
 * Each display row carries:
 *   id              — stable React key (parent.id for atomic / `${parent.id}::${component_code}` for components)
 *   parent_id       — id of the originating line item (used by edit/delete)
 *   product_name    — visible label on the row
 *   product_subtitle— small grey subtitle (frequency, fee_kind, etc.)
 *   software_fee    — INR, contributes to Software & Set-up · Year-1
 *   amc_fee         — INR, contributes to AMC · Year-1
 *   gst_applicable  — bool
 *   gst_percent     — number
 *   option_label    — inherited from parent (for option grouping)
 *   option_description — inherited from parent
 *   kind            — "atomic" | "pkg-itemized" | "pkg-consolidated" | "pkg-hybrid-visible" | "pkg-hybrid-rollup"
 *   is_derived      — true for rows derived from a package (edit acts on parent)
 *   component_count — for rollup rows, how many components were folded in
 */
export function expandLineItem(li) {
    if (!isPackageLine(li)) {
        // Atomic — preserve the legacy single-row shape.
        // Iter 97.12 — Builder-materialised atomic lines carry an optional
        // `scope_modules` snapshot that renders as the "Includes" list under
        // the product name on Proposal Detail / Print. Empty for legacy
        // atomic lines so they continue to render identically.
        // Iter 97.15 — Builder-materialised lines also carry a `row_kind`
        // tag (product_header / module / atomic) and a `presentation_mode`
        // tag (itemized / grouped / consolidated). Both pass through to the
        // display layer so Proposal Detail + Print can apply identical
        // price-visibility rules to the Builder Preview without touching
        // the totals math.
        const builderScope = Array.isArray(li.scope_modules) ? li.scope_modules : [];
        const scopeItems = builderScope
            .filter((m) => m && m.label)
            .map((m) => ({ module_label: String(m.label), channels: null }));
        return [{
            id: li.id,
            parent_id: li.id,
            product_name: li.product_name || "",
            product_subtitle: _atomicSubtitle(li),
            software_fee: resolveSoftwareFee(li),
            amc_fee: resolveAmcFee(li),
            annual_cost_fee: resolveAnnualCostFee(li),
            annual_cost_complimentary: isAnnualCostComplimentary(li),
            gst_applicable: !!li.gst_applicable,
            gst_percent: Number(li.gst_percent) || 0,
            option_label: li.option_label || "",
            option_description: li.option_description || "",
            kind: "atomic",
            is_derived: false,
            row_kind: li.row_kind || null,
            presentation_mode: li.presentation_mode || null,
            delivery_modes: Array.isArray(li.delivery_modes) ? li.delivery_modes : [],
            ...(scopeItems.length > 0 ? { scope_items: scopeItems } : {}),
        }];
    }

    const mode = li.package_presentation_mode || "itemized";
    const components = li.package_components || [];
    const consolidatedLabel = (li.package_consolidated_label || "").trim() || li.product_name || "";
    const headGstPct = Number(li.gst_percent) || 0;
    const headGstAppl = li.gst_applicable !== false;

    if (mode === "itemized") {
        // One row per component.
        return components.map((c) => ({
            id: `${li.id}::${c.component_code}`,
            parent_id: li.id,
            product_name: c.label || c.component_code || "(unnamed component)",
            product_subtitle: _componentSubtitle(c, li),
            software_fee: _componentSoftwareFee(c),
            amc_fee: _componentAmcFee(c),
            gst_applicable: c.gst_percent != null ? true : headGstAppl,
            gst_percent: c.gst_percent != null ? Number(c.gst_percent) : headGstPct,
            option_label: li.option_label || "",
            option_description: li.option_description || "",
            kind: "pkg-itemized",
            is_derived: true,
        }));
    }

    if (mode === "consolidated") {
        // Single bundled row — sum across all components, presented as one
        // commercial line. GST uses the head's gst_percent for C.1.2a
        // (weighted blend across per-component rates is C.1.2b PDF scope).
        const software = components.reduce((s, c) => s + _componentSoftwareFee(c), 0);
        const amc = components.reduce((s, c) => s + _componentAmcFee(c), 0);
        return [{
            id: `${li.id}::__consolidated__`,
            parent_id: li.id,
            product_name: consolidatedLabel,
            product_subtitle: `Package · Consolidated · ${components.length} component${components.length === 1 ? "" : "s"} bundled`,
            software_fee: software,
            amc_fee: amc,
            gst_applicable: headGstAppl,
            gst_percent: headGstPct,
            option_label: li.option_label || "",
            option_description: li.option_description || "",
            kind: "pkg-consolidated",
            is_derived: true,
            // Iter C.1.2d — Client-facing scope. The bundled commercial value
            // is the priced row; the scope list immediately beneath surfaces
            // the modules included WITHOUT separate prices, satisfying the
            // "one bundled offering + supporting scope" framing.
            scope_items: _buildScopeItems(components),
        }];
    }

    // hybrid — visible components inline + 1 rollup row for the rest.
    const visibleComps = components.filter((c) => c.is_visible_in_print !== false);
    const hiddenComps = components.filter((c) => c.is_visible_in_print === false);
    const rows = visibleComps.map((c) => ({
        id: `${li.id}::${c.component_code}`,
        parent_id: li.id,
        product_name: c.label || c.component_code || "(unnamed component)",
        product_subtitle: _componentSubtitle(c, li),
        software_fee: _componentSoftwareFee(c),
        amc_fee: _componentAmcFee(c),
        gst_applicable: c.gst_percent != null ? true : headGstAppl,
        gst_percent: c.gst_percent != null ? Number(c.gst_percent) : headGstPct,
        option_label: li.option_label || "",
        option_description: li.option_description || "",
        kind: "pkg-hybrid-visible",
        is_derived: true,
    }));
    if (hiddenComps.length > 0) {
        const software = hiddenComps.reduce((s, c) => s + _componentSoftwareFee(c), 0);
        const amc = hiddenComps.reduce((s, c) => s + _componentAmcFee(c), 0);
        rows.push({
            id: `${li.id}::__hybrid_rollup__`,
            parent_id: li.id,
            product_name: `Bundled services (${hiddenComps.length} component${hiddenComps.length === 1 ? "" : "s"})`,
            product_subtitle: `Package · Hybrid · Rolled up from ${li.product_name || "package"}`,
            software_fee: software,
            amc_fee: amc,
            gst_applicable: headGstAppl,
            gst_percent: headGstPct,
            option_label: li.option_label || "",
            option_description: li.option_description || "",
            kind: "pkg-hybrid-rollup",
            is_derived: true,
            component_count: hiddenComps.length,
            // Iter C.1.2d — Scope of the bundled (hidden) components only.
            // The visible components are already priced rows above and need
            // no further scope mention; this scope list lets the client see
            // *what* is bundled into the rolled-up line.
            scope_items: _buildScopeItems(hiddenComps),
        });
    }
    return rows;
}

/**
 * Expand an entire version's line_items into display rows, preserving the
 * original line-item order (each parent line item's expansion stays contiguous).
 */
export const expandLineItems = (line_items) =>
    (line_items || []).flatMap((li) => expandLineItem(li));

/* ──────────────────────────────────────────────────────────────────────── */
/* Subtitle helpers — kept local so the table renderer stays terse.         */

function _atomicSubtitle(li) {
    // Owner directive 2026-09-29 — the subtitle slot under the product name
    // carries the product note (catalogue description for Master rows,
    // operator-written "About this product" for custom rows). The legacy
    // "One-time" product_type fallback is removed: no note → no subtitle.
    return (li.description || "").trim();
}

function _componentSubtitle(c, _li) {
    const parts = [];
    if (c.fee_kind) parts.push(_humanFeeKind(c.fee_kind));
    if (c.billing_frequency) parts.push(c.billing_frequency);
    if (c.activation_rule && c.activation_rule !== "immediate") {
        parts.push(_humanActivationRule(c.activation_rule, c.free_support_months));
    }
    // Iter C.1.2c — Surface delivery surfaces as a short suffix (e.g.
    // "Browser/Android/iOS") so operators can scan channel coverage at
    // a glance. Empty/null is silent (atomic-equivalent behaviour).
    if (Array.isArray(c.delivery_channels) && c.delivery_channels.length) {
        parts.push(c.delivery_channels.map(_humanChannel).join("/"));
    }
    return parts.join(" · ");
}

function _humanChannel(ch) {
    const map = { browser: "Browser", android: "Android", ios: "iOS", api: "API", server: "Server" };
    return map[ch] || ch;
}

/**
 * Iter C.1.2d — Auto-derived scope list for bundled rendering.
 *
 * Groups package_components by `source_catalogue_id` (operator's master-data
 * link per Phase C immutability principle D1) and dedupes Implementation /
 * AMC pairs that share the same source so the client sees each module once.
 *
 * Module label resolution:
 *   • Take the first component in the group as representative.
 *   • If its label contains " — " (em-dash with surrounding spaces),
 *     strip the suffix (e.g. "Listed Equity — Implementation" → "Listed Equity").
 *   • Otherwise use the full label as-is.
 *
 * Channel resolution:
 *   • Union the `delivery_channels` across all components in the group,
 *     preserving first-occurrence order so the chip ordering stays stable
 *     across re-renders.
 *
 * Defensive: components missing `source_catalogue_id` (should not happen in
 * package mode but legacy fixtures might) fall back to grouping by
 * `component_code` so they still produce a sensible scope entry.
 *
 * Returns an array of `{ module_label, channels }` objects in the order
 * each source's first component appears in the input list.
 */
export function _buildScopeItems(components) {
    if (!Array.isArray(components) || components.length === 0) return [];
    const groups = new Map();
    for (const c of components) {
        const key = c.source_catalogue_id || c.component_code || "__noop__";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
    }
    const items = [];
    for (const [, group] of groups.entries()) {
        const rep = group[0];
        const fullLabel = rep.label || rep.component_code || "";
        const dashIdx = fullLabel.indexOf(" — ");
        const module_label = dashIdx > 0 ? fullLabel.slice(0, dashIdx).trim() : fullLabel.trim();
        const channelSet = new Set();
        const channels = [];
        for (const c of group) {
            for (const ch of (c.delivery_channels || [])) {
                if (!channelSet.has(ch)) {
                    channelSet.add(ch);
                    channels.push(ch);
                }
            }
        }
        items.push({
            module_label: module_label || "(unnamed module)",
            channels: channels.length ? channels : null,
        });
    }
    return items;
}

/**
 * Iter C.1.2d — Format a single scope item's channel list as a short
 * human-readable suffix, e.g. `["browser","android","ios"]` →
 * "Browser/Android/iOS". Returns "" when there are no channels so the
 * caller can decide whether to show a parenthetical at all.
 */
export function formatScopeChannels(channels) {
    if (!Array.isArray(channels) || channels.length === 0) return "";
    return channels.map(_humanChannel).join("/");
}

function _humanFeeKind(k) {
    const map = {
        implementation: "Implementation",
        migration: "Migration",
        amc: "AMC",
        hosting: "Hosting",
        support: "Support",
        subscription: "Subscription",
        customisation: "Customisation",
        training: "Training",
        integration: "Integration",
        other: "Other",
    };
    return map[k] || k;
}

function _humanActivationRule(r, freeMonths) {
    if (r === "go_live") return "@Go-Live";
    if (r === "project_completion") return "@Project completion";
    if (r === "project_completion_plus_free_support") {
        return `@Completion +${freeMonths || "?"}mo free support`;
    }
    if (r === "project_completion_plus_one_year") return "@Completion +1yr";
    if (r === "manual") return "Manual";
    return r;
}
